import React from "react";
import { StyleSheet, View, Text, Pressable } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { emitSpotlightEvent } from "../../Widgets/SpotlightTutorial/spotlightBus";
import { SPOTLIGHT_EVENTS } from "../../Widgets/SpotlightTutorial/spotlightEventNames";
import { SPOTLIGHT_TARGETS, spotlightTarget } from "../../Widgets/SpotlightTutorial/spotlightTargets";

const HOW_IT_WORKS_DETAILS = [
    {
        title: "1. Create or connect your Stripe account",
        text: "Stripe is the payment provider that handles Grad Fund payments. We'll send you to Stripe to create a new account or connect one you already have.",
    },
    {
        title: "2. Complete Stripe's verification",
        text: "Stripe will ask for a few details, such as your name, date of birth, and the bank account where you'd like to receive payouts. This is a standard step that helps keep payments safe.",
    },
    {
        title: "3. Return to MyTassel",
        text: "Once Stripe confirms your account, come back here and your Grad Fund is ready. Friends and family can start giving toward your Graduation Fund right away.",
    },
];

const StripeTrustNotice = () => (
    <View style={styles.trustContainer}>
        <Ionicons name="lock-closed" size={22} color="#4c956c" style={styles.trustIcon} />
        <View style={styles.trustTextContainer}>
            <Text style={styles.trustTitle}>Secure Payments Powered by Stripe</Text>
            <Text style={styles.trustText}>Your banking information is never stored by MyTassel.</Text>
        </View>
    </View>
);

const HowItWorksDetails = () => (
    <View style={styles.learnDetails}>
        {HOW_IT_WORKS_DETAILS.map(({ title, text }) => (
            <View key={title} style={styles.learnItem}>
                <Text style={styles.learnItemTitle}>{title}</Text>
                <Text style={styles.learnItemText}>{text}</Text>
            </View>
        ))}
    </View>
);

const ModalCloseLink = ({ label, onPress, ...pressableProps }) => (
    <Pressable onPress={onPress} style={styles.closeLink} accessibilityRole="button" {...pressableProps}>
        <Text style={styles.closeLinkText}>{label}</Text>
    </Pressable>
);

const SetupSecondaryActions = ({ isHowItWorksExpanded, onToggleHowItWorks, onSkip }) => {
    const handleSkip = () => {
        onSkip();
        emitSpotlightEvent(SPOTLIGHT_EVENTS.GRADFUND_SETUP_SKIPPED);
    };

    return (
        <View style={styles.secondaryActions}>
            <View style={styles.secondaryActionsRow}>
                <Pressable onPress={onToggleHowItWorks} style={styles.closeLink} accessibilityRole="button">
                    <Text style={styles.learnLink}>{isHowItWorksExpanded ? "Hide Details" : "Learn How It Works"}</Text>
                </Pressable>
                <ModalCloseLink label="Skip for Now" onPress={handleSkip} {...spotlightTarget(SPOTLIGHT_TARGETS.GRADFUND_SETUP_SKIP)} />
            </View>
            {isHowItWorksExpanded && <HowItWorksDetails />}
        </View>
    );
};

const styles = StyleSheet.create({
    trustContainer: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        padding: 14,
        marginBottom: 15,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(76, 149, 108, 0.3)",
        backgroundColor: "rgba(255, 255, 255, 0.6)",
    },
    trustIcon: {
        marginRight: 12,
    },
    trustTextContainer: {
        flex: 1,
    },
    trustTitle: {
        fontSize: 15,
        fontWeight: "600",
        color: "#2f5138",
        marginBottom: 2,
    },
    trustText: {
        fontSize: 13,
        color: "#666",
        lineHeight: 18,
    },
    secondaryActions: {
        width: "100%",
        alignItems: "center",
    },
    secondaryActionsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        columnGap: 16,
    },
    learnLink: {
        fontSize: 14,
        fontWeight: "600",
        color: "#4c956c",
        textDecorationLine: "underline",
    },
    learnDetails: {
        width: "100%",
        marginTop: 8,
        padding: 16,
        borderRadius: 10,
        backgroundColor: "rgba(76, 149, 108, 0.08)",
    },
    learnItem: {
        marginBottom: 12,
    },
    learnItemTitle: {
        fontSize: 14,
        fontWeight: "600",
        color: "#2f5138",
        marginBottom: 2,
    },
    learnItemText: {
        fontSize: 13,
        color: "#555",
        lineHeight: 18,
    },
    closeLink: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        marginTop: 4,
    },
    closeLinkText: {
        fontSize: 14,
        color: "#777",
        textDecorationLine: "underline",
    },
});

export { StripeTrustNotice, SetupSecondaryActions, ModalCloseLink };
